import type { QueryKey } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type {
  ThreadServiceSendMessageBody,
  ThreadServiceSendToolCallBody,
  ThreadServiceSendToolResultBody,
  V1ArtifactContentResponse,
  V1GetEventsResponse,
  V1SendToolCallResponse,
} from '@/generated/hooks/rain/models';

import type { IRainAgentConfig, IRainThreadAdapter } from './scope';

/**
 * The scope-specific pieces each adapter resolves (by calling its own scoped
 * generated hooks) and hands to `buildRainAdapter`. Everything that is NOT
 * scope-specific — the `useCallback` wrapping, the events normalization, and
 * the assembly of the uniform `IRainThreadAdapter` shape — lives in the helper
 * rather than in each scope adapter.
 *
 * The mutation/fetch functions here are expected to be pre-bound to the scope's
 * ids by the caller (i.e. the adapter closes over organization/workspace/etc.),
 * so this helper stays scope-agnostic.
 */
export interface IRainAdapterParts {
  agentId: string;
  agentVersion?: string;
  agentConfig: IRainAgentConfig;

  /** Raw backlog events query result (from the scope's GetEvents hook). */
  events: {
    data: AxiosResponse<V1GetEventsResponse> | undefined;
    isFetching: boolean;
    error: unknown;
  };

  /** Scope-bound mutations (already include the scope path ids). */
  sendMessage: (data: ThreadServiceSendMessageBody) => Promise<unknown>;
  sendToolResult: (data: ThreadServiceSendToolResultBody) => Promise<unknown>;
  /** Only the automation scope provides this — see `IRainThreadAdapter.sendToolCall`. */
  sendToolCall?: (
    data: ThreadServiceSendToolCallBody,
  ) => Promise<V1SendToolCallResponse>;
  stop: () => Promise<unknown>;
  wake: () => Promise<unknown>;

  /** Pre-built SSE stream URL for the bound thread (empty until ids ready). */
  streamUrl: string;
  /** Parent resource for uploads; see `IRainThreadAdapter.attachmentScope`. */
  attachmentScope?: string;

  /** Scope-bound query-key + artifact-content resolvers. */
  getActivePlanQueryKey: () => QueryKey;
  fetchArtifactContent: (
    artifactUri: string,
  ) => Promise<AxiosResponse<V1ArtifactContentResponse>>;
  getArtifactContentQueryKey: (artifactUri: string) => QueryKey;
}

/**
 * Assemble the uniform `IRainThreadAdapter` consumed by `useRainThreadMessages`
 * from a scope's resolved parts. Pure — no hooks — so it composes with the
 * rules-of-hooks-bound generated hooks the caller invokes.
 */
export function buildRainAdapter(parts: IRainAdapterParts): IRainThreadAdapter {
  return {
    agentId: parts.agentId,
    agentVersion: parts.agentVersion,
    agentConfig: parts.agentConfig,
    events: {
      data: parts.events.data,
      isFetching: parts.events.isFetching,
      error: parts.events.error as Error | null,
    },
    sendMessage: parts.sendMessage,
    sendToolResult: parts.sendToolResult,
    sendToolCall: parts.sendToolCall,
    stop: parts.stop,
    wake: parts.wake,
    streamUrl: parts.streamUrl,
    attachmentScope: parts.attachmentScope,
    getActivePlanQueryKey: parts.getActivePlanQueryKey,
    fetchArtifactContent: parts.fetchArtifactContent,
    getArtifactContentQueryKey: parts.getArtifactContentQueryKey,
  };
}
